import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { api, formatApiError } from '@/lib/api';
import { toast } from 'sonner';
import { EmptyState, PageHeader, Spinner } from '@/components/Common';
import {
  BellRingingIcon,
  CheckCircleIcon,
  ClockCounterClockwiseIcon,
  WarningCircleIcon,
  PaperPlaneTiltIcon,
  ArrowRightIcon,
  EnvelopeSimpleIcon,
} from '@phosphor-icons/react';

const TABS = [
  { key: 'pending', label: 'Upcoming' },
  { key: 'overdue', label: 'Overdue' },
  { key: 'sent', label: 'Sent' },
  { key: 'handled', label: 'Handled' },
];

export default function RemindersPage() {
  const [items, setItems] = useState(null);
  const [tab, setTab] = useState('pending');
  const [busy, setBusy] = useState(false);

  async function load() {
    try {
      const { data } = await api.get('/reminders');
      setItems(data);
    } catch (e) {
      toast.error(formatApiError(e));
      setItems([]);
    }
  }
  useEffect(() => {
    load();
  }, []);

  const filtered = useMemo(() => {
    if (!items) return [];
    const today = new Date().toISOString().slice(0, 10);
    if (tab === 'pending') return items.filter((r) => r.status === 'pending' && r.reminder_date > today);
    if (tab === 'overdue') return items.filter((r) => r.status === 'pending' && r.reminder_date <= today);
    return items.filter((r) => r.status === tab);
  }, [items, tab]);

  const counts = useMemo(() => {
    if (!items) return { pending: 0, overdue: 0, sent: 0, handled: 0 };
    const today = new Date().toISOString().slice(0, 10);
    return {
      pending: items.filter((r) => r.status === 'pending' && r.reminder_date > today).length,
      overdue: items.filter((r) => r.status === 'pending' && r.reminder_date <= today).length,
      sent: items.filter((r) => r.status === 'sent').length,
      handled: items.filter((r) => r.status === 'handled').length,
    };
  }, [items]);

  async function action(r, kind) {
    try {
      const fd = new FormData();
      fd.append('action', kind);
      await api.patch(`/reminders/${r.id}`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success(kind === 'handled' ? 'Marked handled' : 'Snoozed 30 days');
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }

  async function toggleClient(r) {
    try {
      const fd = new FormData();
      fd.append('notify_client', r.notify_client ? 'false' : 'true');
      await api.patch(`/reminders/${r.id}`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }

  async function runNow() {
    if (!window.confirm('Manually run the reminder scheduler now? Sends any reminders whose date is today or earlier.')) return;
    setBusy(true);
    try {
      const { data } = await api.post('/reminders/run_now');
      toast.success(`Scanned ${data.scanned}, sent ${data.sent}, failed ${data.failed}`);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <PageHeader
        eyebrow="Compliance Calendar"
        title="Reminders"
        subtitle="Auto-generated from your delivered engagements. Annual training, fit tests, OSHA postings, and follow-up visits."
        right={
          <button className="gl-btn gl-btn-secondary !h-10 !px-4 !text-xs" onClick={runNow} disabled={busy} data-testid="reminders-run-now">
            <PaperPlaneTiltIcon size={16} weight="bold" /> {busy ? 'Running…' : 'Run Now'}
          </button>
        }
      />

      <section className="grid grid-cols-2 md:grid-cols-4 gap-3 px-5 md:px-8 mt-2">
        {[
          { label: 'Upcoming', val: counts.pending, bg: 'var(--gl-navy)', fg: '#fff' },
          { label: 'Overdue', val: counts.overdue, bg: 'var(--gl-p1)', fg: '#fff' },
          { label: 'Sent', val: counts.sent, bg: 'var(--gl-gold)', fg: 'var(--gl-dark)' },
          { label: 'Handled', val: counts.handled, bg: 'var(--gl-p3)', fg: '#fff' },
        ].map((s) => (
          <div key={s.label} className="p-4 rounded-sm" style={{ background: s.bg, color: s.fg }}>
            <div className="text-xs font-bold uppercase tracking-widest opacity-80" style={{ fontFamily: 'Inter' }}>
              {s.label}
            </div>
            <div className="font-display font-black text-4xl leading-none mt-1">{s.val}</div>
          </div>
        ))}
      </section>

      <div className="mt-6 border-b-2 border-gray-200 flex gap-2 px-5 md:px-8 overflow-x-auto hide-scrollbar">
        {TABS.map((t) => (
          <button
            key={t.key}
            className={`gl-tab ${tab === t.key ? 'active' : ''}`}
            onClick={() => setTab(t.key)}
            data-testid={`reminders-tab-${t.key}`}
          >
            {t.label} ({counts[t.key]})
          </button>
        ))}
      </div>

      <section className="px-5 md:px-8 py-6">
        {items === null ? (
          <Spinner label="Loading reminders…" />
        ) : filtered.length === 0 ? (
          <EmptyState
            icon={BellRingingIcon}
            title={tab === 'pending' ? 'No upcoming reminders' : tab === 'overdue' ? 'No overdue reminders' : `Nothing ${tab}`}
            body="Reminders auto-generate when you mark an engagement Delivered."
          />
        ) : (
          <div className="space-y-3">
            {filtered.map((r) => (
              <ReminderRow key={r.id} r={r} onAction={action} onToggleClient={toggleClient} />
            ))}
          </div>
        )}
      </section>
    </>
  );
}

function ReminderRow({ r, onAction, onToggleClient }) {
  const today = new Date().toISOString().slice(0, 10);
  const isOverdue = r.status === 'pending' && r.reminder_date <= today;
  return (
    <div
      className="gl-card flex flex-col md:flex-row gap-3 items-start md:items-center"
      style={{ borderLeft: `4px solid ${isOverdue ? 'var(--gl-p1)' : r.status === 'sent' ? 'var(--gl-gold)' : r.status === 'handled' ? 'var(--gl-p3)' : 'var(--gl-navy)'}` }}
      data-testid={`reminder-row-${r.id}`}
    >
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-display font-extrabold uppercase tracking-widest" style={{ color: 'var(--gl-dark)' }}>
            {r.item_label}
          </span>
          {isOverdue && (
            <span className="gl-chip gl-chip-p1">
              <WarningCircleIcon size={12} weight="fill" /> OVERDUE
            </span>
          )}
          {r.recurring_months ? (
            <span className="gl-chip" style={{ background: '#e5e7eb', color: '#1f2937' }}>RECURRING</span>
          ) : (
            <span className="gl-chip" style={{ background: '#e5e7eb', color: '#1f2937' }}>ONE-TIME</span>
          )}
        </div>
        <div className="text-sm mt-1" style={{ color: 'var(--gl-muted)' }}>
          <b style={{ color: 'var(--gl-dark)' }}>{r.engagement_business_name}</b>
          {r.engagement_contact_name && <> · {r.engagement_contact_name}</>}
          {r.engagement_contact_phone && <> · {r.engagement_contact_phone}</>}
        </div>
        <div className="text-xs uppercase tracking-widest mt-1 font-bold" style={{ color: 'var(--gl-navy)', fontFamily: 'Inter', letterSpacing: '0.1em' }}>
          Remind {r.reminder_date} · Due {r.due_date}
          {r.snoozed_count > 0 && <> · Snoozed {r.snoozed_count}×</>}
        </div>
      </div>

      <div className="flex flex-wrap gap-2 items-center">
        <Link to={`/engagements/${r.engagement_id}`} className="gl-btn gl-btn-secondary !h-10 !px-3 !text-xs">
          Open <ArrowRightIcon size={14} weight="bold" />
        </Link>
        <label
          className="flex items-center gap-1.5 px-3 py-1.5 border-2 rounded-sm cursor-pointer text-xs uppercase tracking-wider font-bold"
          style={{ borderColor: r.notify_client ? 'var(--gl-navy)' : '#d1d5db', background: r.notify_client ? '#eff4fb' : '#fff', color: 'var(--gl-dark)', fontFamily: 'Inter' }}
          title={r.notify_client ? 'Client will be emailed' : 'Vince only'}
        >
          <input type="checkbox" checked={!!r.notify_client} onChange={() => onToggleClient(r)} data-testid={`reminder-notify-client-${r.id}`} />
          <EnvelopeSimpleIcon size={14} weight="bold" /> Notify Client
        </label>
        {r.status === 'pending' && (
          <>
            <button className="gl-btn gl-btn-secondary !h-10 !px-3 !text-xs" onClick={() => onAction(r, 'snooze')} data-testid={`reminder-snooze-${r.id}`}>
              <ClockCounterClockwiseIcon size={14} weight="bold" /> Snooze 30d
            </button>
            <button className="gl-btn gl-btn-accent !h-10 !px-3 !text-xs" onClick={() => onAction(r, 'handled')} data-testid={`reminder-handled-${r.id}`}>
              <CheckCircleIcon size={14} weight="bold" /> Handled
            </button>
          </>
        )}
      </div>
    </div>
  );
}
